import { ReactNode, useEffect, useState } from 'react';
import centralIcon from '../assets/central-icon.png';
import { Icon, type IconName } from './admin/AdminUI';
import { Link, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../hooks/useAppDispatch';
import { clearToken } from '../store/authSlice';
import { performCentralAdminLogout, readAdminToken } from '../utils/sessionRecovery';
import { USER_HOME_PATH } from '../utils/adminAccess';

interface LayoutProps {
  children: ReactNode;
}

/**
 * The suite shell, as every LevelUp app shows it (@levelup/ui-shell's
 * SuiteShell in Outbound, CRM, WeOpen…): a coloured frame, a white rounded top
 * bar with the app mark and name, a white rounded navigation card with the
 * profile area at its foot, and the page on a light rounded content card.
 *
 * Replicated here rather than imported: the package is distributed from the
 * private registry and needs a suite adapter (switch-context, notifications)
 * the admin console does not have. The geometry and colours are the shell's
 * own defaults: 16px gutter, 64px header, 256px navigation, 12px radii,
 * frame #1a3a4a, active item #e8f4f8, content #faf9f7.
 */
const SHELL = {
  frame: '#1a3a4a',
  activeItem: '#e8f4f8',
  activeIcon: '#1a3a4a',
  content: '#faf9f7',
} as const;

const navGroups: Array<{ label: string; items: Array<{ path: string; label: string; icon: IconName }> }> = [
  {
    label: 'People',
    items: [
      { path: '/users', label: 'Users', icon: 'users' },
      { path: '/workspaces', label: 'Workspaces', icon: 'workspaces' },
    ],
  },
  {
    label: 'Platform',
    items: [
      { path: '/', label: 'Overview', icon: 'overview' },
      { path: '/apps', label: 'Apps Status', icon: 'apps' },
      { path: '/billing', label: 'Billing', icon: 'billing' },
    ],
  },
  {
    label: 'Messaging',
    items: [
      { path: '/tokens', label: 'Push Tokens', icon: 'tokens' },
      { path: '/attempts', label: 'Delivery Attempts', icon: 'attempts' },
    ],
  },
  {
    label: 'Account',
    items: [{ path: '/users/email-change', label: 'Email Change', icon: 'mail' }],
  },
];

const navItems = navGroups.flatMap((g) => g.items);

/**
 * The nav item for `pathname`: an exact match, else the longest item that is a
 * path prefix, so `/users/<id>` lights up Users while `/users/email-change`
 * still lights up only Email Change. `/` matches only itself.
 */
export function activeNavPath(pathname: string): string | null {
  let best: string | null = null;
  for (const { path } of navItems) {
    const hit = path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`);
    if (hit && (best === null || path.length > best.length)) best = path;
  }
  return best;
}

/** The signed-in admin's email, from the admin token (display only). */
function signedInEmail(): string | null {
  const token = readAdminToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { email?: unknown };
    return typeof payload.email === 'string' ? payload.email : null;
  } catch {
    return null;
  }
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const activePath = activeNavPath(location.pathname);
  const dispatch = useAppDispatch();
  const { isAuthenticated, isDevMode } = useAppSelector((state) => state.auth);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const email = signedInEmail();
  const initial = (email ?? 'A').charAt(0).toUpperCase();

  // Close the mobile drawer whenever the route changes.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  /**
   * EXPLICIT LOGOUT ENDS THE CENTRAL SESSION, not just this tab's copy of it.
   * The server call is a HARD GATE: if the session could not be destroyed we
   * stay put and say so, rather than clearing local state and claiming success.
   */
  const handleLogout = async () => {
    setIsLoggingOut(true);
    setLogoutError('');
    try {
      await performCentralAdminLogout();
      dispatch(clearToken());
    } catch {
      setLogoutError('Could not sign you out. You are still signed in — please try again.');
      setIsLoggingOut(false);
    }
  };

  const nav = (
    <div className="flex h-full flex-col">
      <nav aria-label="Admin" className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {navGroups.map((group) => (
          <div key={group.label}>
            <p className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{group.label}</p>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = activePath === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      aria-current={active ? 'page' : undefined}
                      className={`flex items-center gap-3 rounded-[10px] px-2 py-1 text-sm transition-colors ${
                        active ? 'font-medium text-slate-900' : 'text-slate-700 hover:bg-black/[0.04]'
                      }`}
                      style={active ? { background: SHELL.activeItem } : undefined}
                    >
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] ${
                          active ? 'text-white' : 'text-slate-600'
                        }`}
                        style={active ? { background: SHELL.activeIcon } : undefined}
                      >
                        <Icon name={item.icon} className="h-[18px] w-[18px]" />
                      </span>
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {isAuthenticated && (
        <div className="border-t border-slate-200 p-3 space-y-1">
          {logoutError && (
            <p role="alert" className="mb-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
              {logoutError}
            </p>
          )}
          <div className="flex items-center gap-3 px-2 py-2">
            <span
              aria-hidden="true"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
              style={{ background: SHELL.frame }}
            >
              {initial}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-900">{email ?? 'Central admin'}</p>
              <p className="text-xs text-slate-500">Central admin</p>
            </div>
          </div>
          {/* Admins land here after signing in; their own workspace is
              one click away. /home never redirects back to the console. */}
          <Link
            to={USER_HOME_PATH}
            className="flex items-center gap-3 rounded-[10px] px-3 py-2 text-sm text-slate-700 hover:bg-black/[0.04]"
          >
            <Icon name="home" className="h-[18px] w-[18px] text-slate-500" />
            My workspace
          </Link>
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="flex w-full items-center gap-3 rounded-[10px] px-3 py-2 text-sm text-slate-700 hover:bg-black/[0.04] disabled:opacity-50"
          >
            <Icon name="logout" className="h-[18px] w-[18px] text-slate-500" />
            {isLoggingOut ? 'Signing out…' : 'Logout'}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden" style={{ background: SHELL.frame }}>
      {isDevMode && (
        <div className="h-7 bg-amber-500 text-amber-950 text-center leading-7 text-sm font-medium">
          DEV AUTH MODE - Do not use in production
        </div>
      )}

      <div className={`flex flex-col gap-4 p-4 ${isDevMode ? 'lg:h-[calc(100vh-1.75rem)]' : 'lg:h-full'}`}>
        {/* Top bar */}
        <header className="flex h-16 shrink-0 items-center justify-between gap-3 rounded-xl bg-white px-4">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="rounded-[10px] p-2 text-slate-700 hover:bg-black/[0.04] lg:hidden"
              aria-label="Open navigation"
              aria-expanded={drawerOpen}
            >
              <Icon name="menu" className="h-6 w-6" />
            </button>
            <Link to="/" className="flex min-w-0 items-center gap-3" aria-label="LevelUp Central admin home">
              <img src={centralIcon} alt="" className="h-11 w-11 shrink-0 rounded-[10px] object-contain" />
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-base font-semibold leading-tight text-slate-900">LevelUp Central Admin</span>
                <span className="truncate text-xs leading-tight text-slate-500">Workspace Management</span>
              </span>
            </Link>
          </div>
          <div className="flex items-center gap-1">
            <Link
              to={USER_HOME_PATH}
              title="Your apps"
              aria-label="Your apps"
              className="flex h-10 w-10 items-center justify-center rounded-[10px] text-slate-700 hover:bg-black/[0.04]"
            >
              <Icon name="apps" className="h-5 w-5" />
            </Link>
            <span
              title={email ?? 'Central admin'}
              className="ml-1 flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold text-white"
              style={{ background: SHELL.frame }}
            >
              {initial}
            </span>
          </div>
        </header>

        <div className="flex min-h-0 flex-1 gap-4">
          {/* Mobile drawer backdrop */}
          {drawerOpen && (
            <div
              aria-hidden="true"
              onClick={() => setDrawerOpen(false)}
              className="fixed inset-0 z-30 bg-slate-950/50 lg:hidden"
            />
          )}

          {/* Navigation card: in the frame on desktop, a slide-in card on mobile */}
          <aside
            className={`z-40 w-64 shrink-0 overflow-hidden rounded-xl bg-white transition-transform duration-200 max-lg:fixed max-lg:bottom-4 max-lg:left-4 max-lg:top-4 lg:translate-x-0 ${
              drawerOpen ? 'max-lg:translate-x-0' : 'max-lg:-translate-x-[120%]'
            }`}
          >
            <div className="flex justify-end px-2 pt-2 lg:hidden">
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="rounded-md p-1.5 text-slate-500 hover:bg-black/[0.04]"
                aria-label="Close navigation"
              >
                <Icon name="close" />
              </button>
            </div>
            {nav}
          </aside>

          {/* Content card */}
          <main
            className="min-w-0 flex-1 overflow-y-auto rounded-xl px-4 py-6 sm:px-6 lg:px-8"
            style={{ background: SHELL.content }}
          >
            <div className="mx-auto max-w-7xl">{children}</div>
          </main>
        </div>
      </div>
    </div>
  );
}
